/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useContext, useState} from "react";
import * as emotion from "emotion";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {Dialog_Builder, DialogModule, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {Loader} from "../../../../widgets/Loader";
import {auditBy, DB_Object, PartialProperties} from "@intuitionrobotics/ts-common";
import {PatientGender} from "@app/ir-q-app-common/types/care-center/user";
import {DB_Patient, DefaultTimezone, GenderLabel} from "@app/app-shared/patient";
import {DialogCard} from "../../widget/DialogCard";
import {AddPatientDialogContent} from "./AddPatientDialogContent";
import {PatientsContext} from "./PatientsContext";

type Props = {
    organizations: DB_Organization[];
    existingPatient?: DB_Patient;
}

const content = emotion.css(
    {
        boxSizing: "border-box"
    }
);

const body = emotion.css(
    {
        padding: 20,
        paddingTop: 38,
        width: "100%",
        boxSizing: "border-box"
    }
);

export const showAddPatientDialog = (organizations: DB_Organization[], existingPatient?: DB_Patient) => {
    new Dialog_Builder(
        <AddPatientDialog
            organizations={organizations}
            existingPatient={existingPatient}
        />
    ).setAllowIndirectClosing(true)
        .show();
}

export const AddPatientDialog = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const {create} = useContext(PatientsContext)
    const existingPatient = props.existingPatient;
    const organizations = props.organizations;

    const patientInState: PartialProperties<DB_Patient, keyof DB_Object | "contactId"> = existingPatient || {
        name: "",
        unitId: "",
        organizationId: organizations.length === 1 ? organizations[0]._id : "",
        timezone: DefaultTimezone,
        _created: auditBy("fe-stub"),
        _updated: auditBy("fe-stub")
    };

    const [patient, setPatient] = useState(patientInState)

    return !organizations?.length ?
        <Loader/> :
        <DialogCard
            showX={true}
            loading={loading}
            content={
                <div className={`ll_v_l fill ${content}`}>
                    <div className={`${body} ll_v_c`}>
                        <AddPatientDialogContent
                            viewOnly={!!props.existingPatient}
                            patient={patient}
                            onChange={(value: string | GenderLabel, field: keyof DB_Patient) => {
                                if (field === "gender")
                                    return setPatient(s => ({
                                        ...s,
                                        gender: (value as PatientGender)
                                    }))

                                setPatient(s => ({
                                    ...s,
                                    [field]: value
                                }))
                            }}
                            existingPatient={props.existingPatient}
                        />
                    </div>
                </div>
            }
            saveLabel={props.existingPatient ? "Disabled Edit" : "Add patient"}
            cancelLabel={"Cancel"}
            title={props.existingPatient ? "View Patient / Editing is disabled" : "Add patient"}
            onSave={() => {
                setLoading(true);
                (async () => {
                    const name = patient.name.trim();
                    const unitId = patient.unitId.trim();
                    const organizationId = patient.organizationId;

                    if (!name || !unitId || !organizationId) {
                        setLoading(false);
                        return;
                    }

                    try {
                        // Fake but ok
                        await create(patient as DB_Patient);
                        DialogModule.close();
                    } catch (e) {
                        setLoading(false);
                        ToastModule.toastError("Failed to create patient");
                        console.error(e);
                    }
                })().catch(console.error)
                // Keep dialog open.
                return true;
            }}
            saveEnabled={!!(
                !props.existingPatient &&
                patient.name.trim() &&
                patient.unitId.trim() &&
                patient.organizationId
            )}
        />;


}
